import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { isTallyAllowed } from './lib/tallyAllowlist';
import { isApiKeyAdmin } from './lib/apiKeyAllowlist';
import Layout from './components/Layout';
import Login from './pages/Login';
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ServiceDashboard = lazy(() => import('./pages/ServiceDashboard'));
const PartCodes = lazy(() => import('./pages/PartCodes'));
const PartCodeDetail = lazy(() => import('./pages/PartCodeDetail'));
const Scan = lazy(() => import('./pages/Scan'));
const Requisitions = lazy(() => import('./pages/Requisitions'));
const Indents = lazy(() => import('./pages/Indents'));
const POs = lazy(() => import('./pages/POs'));
const GRNs = lazy(() => import('./pages/GRNs'));
const IssueNotes = lazy(() => import('./pages/IssueNotes'));
const OnSitePurchases = lazy(() => import('./pages/OnSitePurchases'));
const OldPartDispositions = lazy(() => import('./pages/OldPartDispositions'));
const Bills = lazy(() => import('./pages/Bills'));
const AccountsPurchases = lazy(() => import('./pages/AccountsPurchases'));
const Vendors = lazy(() => import('./pages/Vendors'));
// Reports, Service Report, Service Analytics and Recurring/Chargeable are now
// four views inside one hub page. The hub lazy-loads each view itself.
const ReportsHub = lazy(() => import('./pages/ReportsHub'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const PartCodeRequests = lazy(() => import('./pages/PartCodeRequests'));
// Material Requisition consolidated into Indents (process-flow rename); the
// page component stays on disk for now but the route below redirects.
// import MaterialReq from './pages/MaterialReq';
const Deliveries = lazy(() => import('./pages/Deliveries'));
const PublicConfirm = lazy(() => import('./pages/PublicConfirm'));
const IssueConfirm = lazy(() => import('./pages/IssueConfirm'));
const PublicStage6 = lazy(() => import('./pages/PublicStage6'));
const PublicContainerStock = lazy(() => import('./pages/PublicContainerStock'));
const ContainerApproval = lazy(() => import('./pages/ContainerApproval'));
const ContainerReady = lazy(() => import('./pages/ContainerReady'));
const ContainerChecklist = lazy(() => import('./pages/ContainerChecklist'));
const PublicContainerVerify = lazy(() => import('./pages/PublicContainerVerify'));
const ImsLiveStock = lazy(() => import('./pages/ImsLiveStock'));
const Masters = lazy(() => import('./pages/Masters'));
const Transactions = lazy(() => import('./pages/Transactions'));
const RFQs = lazy(() => import('./pages/RFQs'));
const PettyCash = lazy(() => import('./pages/PettyCash'));
const Containers = lazy(() => import('./pages/Containers'));
const VendorDetail = lazy(() => import('./pages/VendorDetail'));
const Efficiency = lazy(() => import('./pages/Efficiency'));
const ApprovalRules = lazy(() => import('./pages/ApprovalRules'));
const PettyCashApprovalRules = lazy(() => import('./pages/PettyCashApprovalRules'));
const MonthlyClose = lazy(() => import('./pages/MonthlyClose'));
const POPrint = lazy(() => import('./pages/POPrint'));
const IndentPrint = lazy(() => import('./pages/print/IndentPrint'));
const GRNPrint = lazy(() => import('./pages/print/GRNPrint'));
const IssueNotePrint = lazy(() => import('./pages/print/IssueNotePrint'));
const VendorCoordination = lazy(() => import('./pages/VendorCoordination'));
const JobOrderTracker = lazy(() => import('./pages/JobOrderTracker'));
const ContainerServiceHistory = lazy(() => import('./pages/ContainerServiceHistory'));
const PmSchedule = lazy(() => import('./pages/PmSchedule'));
const DataActions = lazy(() => import('./pages/DataActions'));
const ServiceMondayReport = lazy(() => import('./pages/ServiceMondayReport'));
const LiveStatus = lazy(() => import('./pages/LiveStatus'));
const TatAnalytics = lazy(() => import('./pages/TatAnalytics'));
const JobCostReview = lazy(() => import('./pages/JobCostReview'));
const TravelPlanning = lazy(() => import('./pages/TravelPlanning'));
const Technicians = lazy(() => import('./pages/Technicians'));
const TechnicianDetail = lazy(() => import('./pages/TechnicianDetail'));
const ImsContainers = lazy(() => import('./pages/ImsContainers'));
const Settings = lazy(() => import('./pages/Settings'));
const TallySync = lazy(() => import('./pages/TallySync'));
// Crystal Public API v1 — key administration (admin-only) and the integration
// docs. Both lazy like every other page; the Kit in particular is a big static
// page and must never be in the initial bundle.
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const IntegrationKit = lazy(() => import('./pages/IntegrationKit'));
import ContainersLayout from './components/ContainersLayout';
import ProcurementLayout from './components/ProcurementLayout';
const ProcurementHome = lazy(() => import('./pages/procurement/ProcurementHome'));
const ProcurementDashboard = lazy(() => import('./pages/procurement/Dashboard'));
const CreateIndent = lazy(() => import('./pages/procurement/CreateIndent'));
const AllIndents = lazy(() => import('./pages/procurement/AllIndents'));
const SalesEnquiries = lazy(() => import('./pages/procurement/SalesEnquiries'));
const IndentDetail = lazy(() => import('./pages/procurement/IndentDetail'));
const ProcurementLogin = lazy(() => import('./pages/procurement/ProcurementLogin'));
const PortaLogin = lazy(() => import('./pages/porta/PortaLogin'));
const PortaPublicIndent = lazy(() => import('./pages/porta/PortaPublicIndent'));
import PortaLayout from './components/PortaLayout';
const PortaHome = lazy(() => import('./pages/porta/PortaHome'));
const PortaCostSheet = lazy(() => import('./pages/porta/PortaCostSheet'));
const PortaOrderTracker = lazy(() => import('./pages/porta/PortaOrderTracker'));
const PortaDashboards = lazy(() => import('./pages/porta/PortaDashboards'));
const PortaPurchaseHub = lazy(() => import('./pages/porta/purchase/PortaPurchaseHub'));
const PortaRequisitionForm = lazy(() => import('./pages/porta/purchase/RequisitionForm'));
const PortaRequisitionDetail = lazy(() => import('./pages/porta/purchase/RequisitionDetail'));
const PortaPODetail = lazy(() => import('./pages/porta/purchase/PODetail'));
const PortaBillDetail = lazy(() => import('./pages/porta/purchase/BillDetail'));
const ProcurementVendors = lazy(() => import('./pages/procurement/Vendors'));
const PortaCabinForm = lazy(() => import('./pages/procurement/PortaCabinForm'));
const VendorComparison = lazy(() => import('./pages/procurement/VendorComparison'));
const VendorQuotePortal = lazy(() => import('./pages/procurement/VendorQuotePortal'));
const AccountsSimsPortal = lazy(() => import('./pages/procurement/AccountsSimsPortal'));
const LogisticsArrivalPortal = lazy(() => import('./pages/procurement/LogisticsArrivalPortal'));
const TrackConfirmPortal = lazy(() => import('./pages/procurement/TrackConfirmPortal'));
const GateInPortal = lazy(() => import('./pages/procurement/GateInPortal'));
const ProcurementQueue = lazy(() => import('./pages/procurement/Queue'));
const ProcurementFinanceDesk = lazy(() => import('./pages/procurement/FinanceDesk'));
const ProcurementContainerCosts = lazy(() => import('./pages/procurement/ContainerCosts'));
const ProcurementApprovals = lazy(() => import('./pages/procurement/Approvals'));
const ReplenishmentDesk = lazy(() => import('./pages/procurement/ReplenishmentDesk'));
const VehiclePlacement = lazy(() => import('./pages/procurement/VehiclePlacement'));
const ClientOrders = lazy(() => import('./pages/procurement/ClientOrders'));
const ApprovalSettings = lazy(() => import('./pages/procurement/ApprovalSettings'));
const PublicApproval = lazy(() => import('./pages/procurement/PublicApproval'));
const PiBills = lazy(() => import('./pages/procurement/PiBills'));
const PiBillForm = lazy(() => import('./pages/procurement/PiBillForm'));
const PiBillApproval = lazy(() => import('./pages/procurement/PiBillApproval'));
const GatePortal = lazy(() => import('./pages/procurement/GatePortal'));
const MondayReport = lazy(() => import('./pages/procurement/MondayReport'));
const LogisticsDesk = lazy(() => import('./pages/procurement/LogisticsDesk'));
const QuotationEntry = lazy(() => import('./pages/procurement/QuotationEntry'));
const OmniSearch = lazy(() => import('./pages/procurement/OmniSearch'));
const InventoryStats = lazy(() => import('./pages/procurement/InventoryStats'));
const ImportQueue = lazy(() => import('./pages/procurement/ImportQueue'));
const VesselTracker = lazy(() => import('./pages/procurement/VesselTracker'));
const PortaCabinBuild = lazy(() => import('./pages/procurement/PortaCabinBuild'));
const ProcurementTeam = lazy(() => import('./pages/procurement/ProcurementTeam'));
const ProcurementPnl = lazy(() => import('./pages/procurement/ProcurementPnl'));
const PortaCabinList = lazy(() => import('./pages/procurement/PortaCabinList'));
import { currentRealm, realmHome } from './lib/realm';
const ContainersOverview = lazy(() => import('./pages/containers/Overview'));
const ContainersOnLease = lazy(() => import('./pages/containers/OnLease'));
const ContainersSold = lazy(() => import('./pages/containers/Sold'));
const ContainersOffLease = lazy(() => import('./pages/containers/OffLease'));
const ContainersTransactions = lazy(() => import('./pages/containers/Transactions'));
const ContainersFinancials = lazy(() => import('./pages/containers/Financials'));
const ContainersVendors = lazy(() => import('./pages/containers/Vendors'));
const ContainersExceptions = lazy(() => import('./pages/containers/Exceptions'));
const ContainersReconciliation = lazy(() => import('./pages/containers/Reconciliation'));
const ContainersAging = lazy(() => import('./pages/containers/Aging'));
const ContainersStockHealth = lazy(() => import('./pages/containers/StockHealth'));
const ContainersAnalytics = lazy(() => import('./pages/containers/ContainerAnalytics'));
const ContainersActivity = lazy(() => import('./pages/containers/Activity'));
const ContainersFinder = lazy(() => import('./pages/containers/Finder'));
const ContainersExchange = lazy(() => import('./pages/containers/ExchangeOfMachine'));
const ServiceRequests = lazy(() => import('./pages/ServiceRequests'));
import { DocStackProvider } from './components/DocStack';
import { Loading } from './components/Loading';
import { isPathAllowed, currentProduct, pathMatchesProduct, isRestrictedPath, mayUseRestricted } from './lib/pageCatalog';
import { useLocation } from 'react-router-dom';

function Protected({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    const variant = location.pathname.startsWith('/ims') ? 'containers' : 'default';
    return <Loading size="lg" variant={variant} />;
  }
  if (!user) {
    // Subdomain bias for un-authed users — purchase.crystalgrp.xyz visitors
    // land on the dedicated Procurement login, not the Spares one. Deep
    // links to other realms (/ims/...) keep the default Spares login.
    if (currentRealm() === 'porta' || location.pathname.startsWith('/porta')) {
      return <Navigate to="/porta/login" replace />;
    }
    if (currentRealm() === 'procurement' || location.pathname.startsWith('/procurement')) {
      return <Navigate to="/procurement/login" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  // Realm guard. Backend already 403s cross-realm API calls; this is the
  // UI mirror so a user who deep-links into the wrong realm bounces back to
  // their own home. Three realms today: Spares (default), IMS Containers
  // (imsOnly), and Procurement Portal (procurementRole !== null). Each one
  // owns a URL prefix; cross-realm navigation redirects to the realm home.
  const onImsPath = location.pathname.startsWith('/ims');
  const onProcurementPath = location.pathname.startsWith('/procurement');
  // Cross-realm grant — a dual-realm account (e.g. purchase + IMS) may sit on
  // either realm's paths. `canIms` / `canProc` fold in realmAccess so the guards
  // below don't bounce it off the realm it switched into. Single-realm accounts
  // have empty realmAccess, so their behaviour is unchanged.
  const realms = Array.isArray(user.realmAccess) ? user.realmAccess : [];
  const canIms = user.imsOnly || realms.includes('ims');
  // A global admin (and the superadmin above them) owns every realm — the UI
  // wall must not send them home from a section they administer. Mirrors the
  // same exception the backend realm gate makes.
  const ownsEveryRealm = user.role === 'admin' || user.superAdmin === true;
  const canProc = !!user.procurementRole || realms.includes('procurement') || ownsEveryRealm;
  if (user.imsOnly && !onImsPath && !(realms.includes('procurement') && onProcurementPath)) return <Navigate to={imsHomeFor(user)} replace />;
  if (!canIms && onImsPath) return <Navigate to="/" replace />;
  if (user.procurementRole && !onProcurementPath && !(canIms && onImsPath)) return <Navigate to="/procurement" replace />;
  if (!canProc && onProcurementPath) return <Navigate to="/" replace />;
  const onPortaPath = location.pathname.startsWith('/porta');
  if (user.portaRole && !onPortaPath) return <Navigate to="/porta" replace />;
  if (!user.portaRole && onPortaPath) return <Navigate to="/" replace />;
  // Subdomain bias — when a user lands at the root of a realm subdomain they
  // OWN (e.g. a procurement account on purchase.crystalgrp.xyz, or a porta
  // account on porta.crystalgrp.xyz), nudge them to that realm's home. Guard
  // on actual membership: a Spares account visiting the porta subdomain must
  // NOT be nudged to /porta — the realm guard above would bounce it straight
  // back to '/', an infinite redirect loop. (Non-owning users just stay on the
  // Spares dashboard; the realm guards already routed owning users of the
  // other realms before reaching here.)
  if (location.pathname === '/' && currentRealm() !== 'spares') {
    const r = currentRealm();
    const belongs =
      (r === 'porta' && !!user.portaRole) ||
      (r === 'procurement' && canProc) ||
      (r === 'ims' && !!user.imsOnly);
    if (belongs) return <Navigate to={realmHome(r)} replace />;
  }
  return children;
}

/**
 * Route-level guard for the per-user allowedPages list. Wraps each Layout
 * child so deep-linking to a denied page bounces back to the dashboard
 * (or, if dashboard itself is denied, to the first allowed page).
 *
 * Admins, legacy users (allowedPages == null), and authentication-only
 * routes bypass this entirely. Defense-in-depth — sidebar is also filtered.
 */
function PermittedRoute({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  const location = useLocation();
  // Domain-scoped product wall (spares. vs service.). Applies to EVERYONE
  // (admins included) — the wall is the login domain, not the user role. A
  // spares-only page opened on service. bounces to that product's home.
  const product = currentProduct();
  // Technician logins are exempt: their whole app is three screens (dashboard,
  // own profile, Tech Entry) and the server walls them to their own data. Tech
  // Entry lives on /travel-planning, tagged 'service', so a technician signed
  // in on the spares domain was bounced straight back to the dashboard — "File
  // expenses" and the Tech Entry card appeared to do nothing.
  if (!(user as any)?.technicianId && !pathMatchesProduct(location.pathname, product)) {
    return <Navigate to={product === 'ims' ? '/ims/overview' : '/'} replace />;
  }
  if (!user) return children;
  // A RESTRICTED page is checked before the legacy short-circuit below, which
  // would otherwise hand it to every account whose allowedPages is null.
  if (isRestrictedPath(location.pathname)) {
    return mayUseRestricted(user as any, 'efficiency') ? children : <Navigate to="/" replace />;
  }
  if (user.role === 'admin') return children;
  if (user.allowedPages == null) return children;
  if (isPathAllowed(location.pathname, user.allowedPages)) return children;
  return <Navigate to="/" replace />;
}

/** Sidebar-ordered list of IMS pages with their catalog key + route. The
 *  order matches the NAV_ITEMS list in ContainersLayout so the landing
 *  redirect picks the first tab the user actually has — the same one
 *  they'd see at the top of the sidebar. */
const IMS_PAGE_ORDER: ReadonlyArray<{ key: string; route: string }> = [
  { key: 'ims:overview',     route: '/ims/overview' },
  { key: 'ims:on-lease',     route: '/ims/on-lease' },
  { key: 'ims:sold',         route: '/ims/sold' },
  { key: 'ims:off-lease',    route: '/ims/off-lease' },
  { key: 'ims:transactions', route: '/ims/transactions' },
  { key: 'ims:financials',   route: '/ims/financials' },
  { key: 'ims:vendors',      route: '/ims/vendors' },
  { key: 'ims:exceptions',   route: '/ims/exceptions' },
  { key: 'ims:reconciliation', route: '/ims/reconciliation' },
  { key: 'ims:aging',        route: '/ims/aging' },
  { key: 'ims:stock-health', route: '/ims/stock-health' },
  { key: 'ims:analytics',    route: '/ims/analytics' },
  { key: 'ims:finder',       route: '/ims/finder' },
];

/** Pick the right IMS landing page for a user. Admins + legacy users
 *  (allowedPages == null) and users with the `ims-containers` top-level
 *  shortcut land on /ims/overview by default. Everyone else lands on the
 *  FIRST IMS page they're allowed to see — so Pooja with only Container
 *  Analytics granted lands at /ims/analytics, not at /ims/overview which
 *  she can't see. Falls back to /ims/overview when nothing matches; the
 *  guard below will then bounce back gracefully. */
function imsHomeFor(user: any): string {
  if (!user) return '/ims/overview';
  if (user.role === 'admin') return '/ims/overview';
  const allowed = user.allowedPages;
  if (allowed == null) return '/ims/overview';
  if (!Array.isArray(allowed)) return '/ims/overview';
  if (allowed.includes('ims-containers')) return '/ims/overview';
  for (const entry of IMS_PAGE_ORDER) {
    if (allowed.includes(entry.key)) return entry.route;
  }
  return '/ims/overview';
}

/** True when the user is allowed to load the given IMS route. Mirrors the
 *  sidebar's `isImsPageAllowed` (admins + legacy + the top-level
 *  `ims-containers` shortcut all pass) so a user can never URL-deep-link
 *  past a hidden sidebar item. Defense-in-depth alongside the backend
 *  ACL — without this the sidebar correctly hides /ims/overview but
 *  ContainersLayout still rendered it when the user typed the path. */
function isImsRouteAllowed(user: any, pathname: string): boolean {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const allowed = user.allowedPages;
  if (allowed == null) return true;
  if (!Array.isArray(allowed)) return true;
  if (allowed.includes('ims-containers')) return true;
  for (const entry of IMS_PAGE_ORDER) {
    if (pathname === entry.route || pathname.startsWith(entry.route + '/')) {
      return allowed.includes(entry.key);
    }
  }
  // Settings / Activity / etc. (no catalog entry) fall through to allow —
  // they have their own dedicated guards (AdminOnly).
  return true;
}

/** Route-level guard for IMS pages. Same role as PermittedRoute but the
 *  deny redirect goes to the user's IMS landing instead of "/" — which
 *  would otherwise loop back here because the Protected wrapper redirects
 *  imsOnly users away from "/" to /ims/overview. */
function ImsPermittedRoute({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  const location = useLocation();
  // IMS pages belong to the ims. domain only — on spares./service. bounce home.
  const product = currentProduct();
  if (product === 'spares' || product === 'service') return <Navigate to="/" replace />;
  if (!user) return children;
  if (isImsRouteAllowed(user, location.pathname)) return children;
  const home = imsHomeFor(user);
  // If the user's "home" is the very page that denied them, they have
  // zero IMS grants — show the layout shell with no content. Routing
  // back here would loop, so just render the children and let the empty
  // sidebar speak for itself.
  if (home === location.pathname) return children;
  return <Navigate to={home} replace />;
}

/** Smart landing for `/ims` (and post-login redirect target). Picks the
 *  user's first allowed IMS page so Pooja-with-only-Analytics lands at
 *  /ims/analytics instead of /ims/overview-she-can't-see. */
function ImsHomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={imsHomeFor(user)} replace />;
}

/**
 * Settings guard. Admins get full Settings; service@crystalgroup.in gets the
 * technician-accounts slice only (the page itself renders the restricted view).
 * Everyone else reaching /settings via URL is redirected.
 */
/**
 * API-key minting is NOT an "any admin" capability — a key reads the whole
 * company. Restricted to the named owner; the backend enforces the same list, so
 * this is only the cosmetic half.
 */
function ApiKeyOwnerOnly({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  if (!isApiKeyAdmin(user?.email)) return <Navigate to="/" replace />;
  return children;
}

function AdminOnly({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  const allowed = user && (user.role === 'admin' || (user.email || '').toLowerCase() === 'service@crystalgroup.in');
  if (!allowed) return <Navigate to="/" replace />;
  return children;
}

/**
 * Tally Sync route guard — admin role AND email in the allowlist.
 * Even if someone hardcodes their email into the allowlist client-side,
 * the API still enforces it server-side via TALLY_ALLOWED_EMAILS env.
 */
function TallyAllowed({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  if (!user || user.role !== 'admin' || !isTallyAllowed(user.email)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

export default function App() {
  return (
    <DocStackProvider>
      {/* Every page is lazy-loaded (its own chunk), so the initial bundle is
          just the shell. Suspense shows a spinner while a route chunk streams
          in. Public/login routes stay eager for instant first paint. */}
      <Suspense fallback={<Loading size="lg" />}>
      <Routes>
      <Route path="/login" element={<Login />} />
      {/* Procurement Portal — its own dedicated login. Lives outside the
          Protected wrapper so un-authed users can reach it. The realm
          guard on the Spares /login route ALSO bounces here when the
          host is purchase.crystalgrp.xyz so end-users on the subdomain
          land directly on the procurement branding. */}
      <Route path="/procurement/login" element={<ProcurementLogin />} />
      <Route path="/porta/login" element={<PortaLogin />} />
      {/* PUBLIC Porta-Cabin enquiry — login-free, PIN-gated, embeddable link. */}
      <Route path="/porta-public" element={<PortaPublicIndent />} />
      {/* Integration Kit — PUBLIC on purpose. It is developer documentation: it
          contains no data, makes no authenticated call, and holds no secret. An
          integrator needs to read it BEFORE they have a login (often they never
          get one — they get an API key). Keeping it behind auth just meant
          emailing screenshots of docs around.
          The API KEYS page is the opposite and stays locked to the named owner:
          the docs tell you how to use a key, they do not hand you one. */}
      <Route path="/integration-kit" element={<IntegrationKit />} />
      {/* Procurement Portal — public token-gated routes. No login required;
          the URL token is the auth (validated server-side in
          public.routes.ts). Vendor quotation portal, accounts SIMS form,
          and logistics arrival form. */}
      <Route path="/p/procurement/vendor-quote/:token" element={<VendorQuotePortal />} />
      <Route path="/p/procurement/sims-upload/:token" element={<AccountsSimsPortal />} />
      <Route path="/p/procurement/logistics-arrival/:token" element={<LogisticsArrivalPortal />} />
      <Route path="/p/procurement/track/:action/:token" element={<TrackConfirmPortal />} />
      <Route path="/p/procurement/approval/:token" element={<PublicApproval />} />
      {/* PI → Purchase to Finance: the CEO's / Accounts' personal emailed link. */}
      <Route path="/p/procurement/pi_decide/:token" element={<PiBillApproval mode="token" />} />
      {/* Container Gate In / Gate Out: the depot's emailed link (new in-app portal). */}
      <Route path="/p/procurement/gate-portal/:token" element={<GatePortal mode="token" />} />
      <Route path="/p/procurement/gate-in/:token" element={<GateInPortal />} />
      <Route path="/confirm/:dsNo/:token" element={<PublicConfirm />} />
      <Route path="/issue-confirm/:issueNo/:token" element={<IssueConfirm />} />
      {/* Public Stage 6 fill — bearer token in the URL. Tech opens this
          from email/WhatsApp link; the page bootstraps from the public
          /api/job-orders/public/stage6/:jo/:token route, renders the
          service-log form, and POSTs back to the same path. */}
      <Route path="/p/s6/:token" element={<PublicStage6 />} />
      <Route path="/p/stage6/:jo/:token" element={<PublicStage6 />} />
      {/* Public container-verification page — a verifier's assigned containers,
          opened from an emailed link + PIN. No auth, no sidebar. */}
      <Route path="/p/cv/:token" element={<PublicContainerVerify />} />
      {/* Public container-stock page — no auth, no sidebar. Visitors see
          aggregated availability and can submit block requests. Lives
          outside the Protected wrapper so the route is reachable without
          logging in. */}
      {/* New canonical path. Vite reserves `/public/*` for static-file
          serving, so use a top-level route instead to avoid the warning. */}
      <Route path="/container-stock" element={<PublicContainerStock />} />
      {/* Block Approval Dashboard — PIN-gated (1952) sister page where
          authorised approvers review pending block requests. Sits outside
          the Protected wrapper so approvers don't need a regular login;
          access is controlled by the PIN entered on first visit. */}
      <Route path="/container-approval" element={<ContainerApproval />} />
      {/* Ready Module — PIN-gated (1942) page for yard technicians to
          mark containers ready for dispatch. Live-syncs to the customer
          Stock page (read-only badge) via /availability polling. */}
      <Route path="/container-ready" element={<ContainerReady />} />
      {/* Daily container-stock checklists — PIN-gated (Sitaram 1972,
          Krishna 1982). Each person logs in with their PIN and marks every
          in-stock container Available / Not available with a remark; the
          per-day history is kept for audit. Outside Protected, no sidebar. */}
      <Route path="/checklist-sitaram" element={<ContainerChecklist person="sitaram" />} />
      <Route path="/checklist-krishna" element={<ContainerChecklist person="krishna" />} />
      <Route path="/checklist-accounts" element={<ContainerChecklist person="accounts" />} />
      <Route path="/checklist-pushpalata" element={<ContainerChecklist person="pushpalata" />} />
      <Route path="/checklist-alok" element={<ContainerChecklist person="alok" />} />
      {/* Back-compat redirect for any bookmark / printed link that still
          carries the old `/public/container-stock` URL. */}
      <Route path="/public/container-stock" element={<Navigate to="/container-stock" replace />} />
      {/* IMS Containers module — its own sidebar shell, separate from
          the Spares Layout. Login on the Containers tab redirects here. */}
      <Route
        element={
          <Protected>
            <ContainersLayout />
          </Protected>
        }
      >
        <Route path="/ims" element={<ImsHomeRedirect />} />
        {/* Live Stock — standalone TREE view of the whole live container set
            (Status → Type → Size → Location → unit). Auto-authed via the IMS
            login; the public container-stock page is left untouched. */}
        <Route path="/ims/live-stock" element={<ImsPermittedRoute><ImsLiveStock /></ImsPermittedRoute>} />
        <Route path="/ims/overview" element={<ImsPermittedRoute><ContainersOverview /></ImsPermittedRoute>} />
        <Route path="/ims/on-lease" element={<ImsPermittedRoute><ContainersOnLease /></ImsPermittedRoute>} />
        <Route path="/ims/sold" element={<ImsPermittedRoute><ContainersSold /></ImsPermittedRoute>} />
        <Route path="/ims/off-lease" element={<ImsPermittedRoute><ContainersOffLease /></ImsPermittedRoute>} />
        <Route path="/ims/transactions" element={<ImsPermittedRoute><ContainersTransactions /></ImsPermittedRoute>} />
        <Route path="/ims/financials" element={<ImsPermittedRoute><ContainersFinancials /></ImsPermittedRoute>} />
        <Route path="/ims/vendors" element={<ImsPermittedRoute><ContainersVendors /></ImsPermittedRoute>} />
        <Route path="/ims/exceptions" element={<ImsPermittedRoute><ContainersExceptions /></ImsPermittedRoute>} />
        <Route path="/ims/reconciliation" element={<ImsPermittedRoute><ContainersReconciliation /></ImsPermittedRoute>} />
        <Route path="/ims/aging" element={<ImsPermittedRoute><ContainersAging /></ImsPermittedRoute>} />
        <Route path="/ims/stock-health" element={<ImsPermittedRoute><ContainersStockHealth /></ImsPermittedRoute>} />
        <Route path="/ims/activity" element={<ContainersActivity />} />
        <Route path="/ims/analytics" element={<ImsPermittedRoute><ContainersAnalytics /></ImsPermittedRoute>} />
        <Route path="/ims/finder" element={<ImsPermittedRoute><ContainersFinder /></ImsPermittedRoute>} />
        <Route path="/ims/exchange" element={<ImsPermittedRoute><ContainersExchange /></ImsPermittedRoute>} />
        {/* IMS-side Settings — reuses the same Settings component as the
            Spares side so the new "IMS Container access" toggles are
            manageable without forcing an IMS admin to switch logins. */}
        <Route path="/ims/settings" element={<AdminOnly><Settings /></AdminOnly>} />
      </Route>
      {/* Procurement Portal — third walled-garden realm, served on the
          purchase.crystalgrp.xyz subdomain. Same React bundle; the realm
          guard in <Protected> bounces wrong-realm users out, and the
          backend mirrors that gate in middleware/auth.ts. Routes are
          fleshed out phase by phase; Phase 0 just gives us /procurement
          (landing) so the wiring can be smoke-tested end to end. */}
      <Route
        element={
          <Protected>
            <ProcurementLayout />
          </Protected>
        }
      >
        {/* Procurement landing is now the real Executive Dashboard. The
            old Phase 0 health-probe page is still exported so it can be
            mounted at /procurement/health if needed for ops smoke tests. */}
        <Route path="/procurement" element={<ProcurementDashboard />} />
        <Route path="/procurement/health" element={<ProcurementHome />} />
        <Route path="/procurement/indents" element={<AllIndents />} />
        <Route path="/procurement/indents/new" element={<CreateIndent />} />
        <Route path="/procurement/indents/:indentId" element={<IndentDetail />} />
        <Route path="/procurement/indents/:indentId/compare" element={<VendorComparison />} />
        <Route path="/procurement/indents/:indentId/quote" element={<QuotationEntry />} />
        <Route path="/procurement/vendors" element={<ProcurementVendors />} />
        <Route path="/procurement/efficiency" element={<Efficiency app="purchase" />} />
        <Route path="/procurement/porta-cabin/new" element={<PortaCabinForm />} />
        {/* Phases 2-11 — placeholder lists driven by the backend stub
            endpoints. They paint live data from Mongo today; the action
            buttons (Award, LOI, PO, Payment, Vessel, Approve, etc.) light
            up phase by phase. Each one renders inside ProcurementLayout
            so the sidebar nav stays consistent. */}
        <Route path="/procurement/domestic" element={<ProcurementQueue mode="Domestic" />} />
        <Route path="/procurement/import" element={<ImportQueue />} />
        <Route path="/procurement/vessel-tracker" element={<VesselTracker />} />
        <Route path="/procurement/finance" element={<ProcurementFinanceDesk />} />
        {/* PI → Bill → Purchase to Finance (port of the PI Approval System Apps Script). */}
        <Route path="/procurement/pi-bills" element={<PiBills />} />
        <Route path="/procurement/pi-bills/new" element={<PiBillForm />} />
        <Route path="/procurement/pi-bills/:piId" element={<PiBillApproval />} />
        <Route path="/procurement/gate-portal" element={<GatePortal />} />
        <Route path="/procurement/container-costs" element={<ProcurementContainerCosts />} />
        <Route path="/procurement/logistics" element={<LogisticsDesk />} />
        <Route path="/procurement/approvals" element={<ProcurementApprovals />} />
        <Route path="/procurement/porta-cabin" element={<PortaCabinList />} />
        <Route path="/procurement/porta-cabin/:indentId/build" element={<PortaCabinBuild />} />
        <Route path="/procurement/porta-cabin/:indentId/order" element={<PortaOrderTracker />} />
        <Route path="/procurement/porta-cabin/orders" element={<PortaDashboards />} />
        <Route path="/procurement/team" element={<ProcurementTeam />} />
        <Route path="/procurement/pnl" element={<ProcurementPnl />} />
        <Route path="/procurement/replenishment" element={<ReplenishmentDesk />} />
        <Route path="/procurement/vehicle-placement" element={<VehiclePlacement />} />
        <Route path="/procurement/client-orders" element={<ClientOrders />} />
        <Route path="/procurement/settings" element={<ApprovalSettings />} />
        <Route path="/procurement/monday-report" element={<MondayReport />} />
        <Route path="/procurement/search" element={<OmniSearch />} />
        <Route path="/procurement/sales-enquiries" element={<SalesEnquiries />} />
        <Route path="/procurement/inventory" element={<InventoryStats />} />
      </Route>
      {/* Porta Cabin realm — porta.crystalgrp.xyz, its own login + section. */}
      <Route element={<Protected><PortaLayout /></Protected>}>
        <Route path="/porta" element={<PortaHome />} />
        <Route path="/porta/new" element={<PortaCabinForm />} />
        <Route path="/porta/list" element={<PortaCabinList />} />
        <Route path="/porta/cost-sheet" element={<PortaCostSheet />} />
        <Route path="/porta/:indentId/build" element={<PortaCabinBuild />} />
        <Route path="/porta/:indentId/order" element={<PortaOrderTracker />} />
        <Route path="/porta/orders" element={<PortaDashboards />} />
        {/* Porta purchasing — requisition → PO → goods receipt → bill → paid. */}
        <Route path="/porta/purchase" element={<PortaPurchaseHub />} />
        <Route path="/porta/purchase/requisitions/new" element={<PortaRequisitionForm />} />
        <Route path="/porta/purchase/requisitions/:reqNo/edit" element={<PortaRequisitionForm />} />
        <Route path="/porta/purchase/requisitions/:reqNo" element={<PortaRequisitionDetail />} />
        <Route path="/porta/purchase/pos/:poNo" element={<PortaPODetail />} />
        <Route path="/porta/purchase/bills/:billNo" element={<PortaBillDetail />} />
      </Route>
      {/* Printable PO — kept outside Layout so the sidebar/header don't
          show up in the print preview. Auth still required via Protected. */}
      <Route
        path="/pos/:id/print"
        element={
          <Protected>
            <POPrint />
          </Protected>
        }
      />
      {/* Printable doc pages for the on-site procurement chain — no Layout
          chrome, browser Print → Save as PDF. Opened from the on-site view. */}
      <Route path="/indents/:id/print" element={<Protected><IndentPrint /></Protected>} />
      <Route path="/grns/:id/print" element={<Protected><GRNPrint /></Protected>} />
      <Route path="/issue-notes/:id/print" element={<Protected><IssueNotePrint /></Protected>} />
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/" element={<PermittedRoute>{currentProduct() === 'service' ? <ServiceDashboard /> : <Dashboard />}</PermittedRoute>} />
        <Route path="/scan" element={<PermittedRoute><Scan /></PermittedRoute>} />
        <Route path="/partcodes" element={<PermittedRoute><PartCodes /></PermittedRoute>} />
        <Route path="/p/:code" element={<PermittedRoute><PartCodeDetail /></PermittedRoute>} />
        <Route path="/requisitions" element={<PermittedRoute><Requisitions /></PermittedRoute>} />
        <Route path="/indents" element={<PermittedRoute><Indents /></PermittedRoute>} />
        <Route path="/pos" element={<PermittedRoute><POs /></PermittedRoute>} />
        <Route path="/grns" element={<PermittedRoute><GRNs /></PermittedRoute>} />
        <Route path="/issue-notes" element={<PermittedRoute><IssueNotes /></PermittedRoute>} />
        <Route path="/onsite" element={<PermittedRoute><OnSitePurchases /></PermittedRoute>} />
        <Route path="/old-part-dispositions" element={<PermittedRoute><OldPartDispositions /></PermittedRoute>} />
        <Route path="/bills" element={<PermittedRoute><Bills /></PermittedRoute>} />
        {/* Live mirror of the Accounts/FMS PURCHASE tab. /accounts redirects
            to the canonical /accounts/purchases path. */}
        <Route path="/accounts" element={<Navigate to="/accounts/purchases" replace />} />
        <Route path="/accounts/purchases" element={<PermittedRoute><AccountsPurchases /></PermittedRoute>} />
        <Route path="/vendors" element={<PermittedRoute><Vendors /></PermittedRoute>} />
        <Route path="/vendors/:id" element={<PermittedRoute><VendorDetail /></PermittedRoute>} />
        {/* Efficiency Intelligence — Service and Purchase, scored independently. */}
        {/* Service Desk shows SERVICE efficiency. Purchase lives in the
            Purchase dashboard — see /procurement/efficiency below. */}
        <Route path="/efficiency" element={<PermittedRoute><Efficiency app="service" /></PermittedRoute>} />
        {/* One "Reports & Analytics" hub. The three old routes still resolve —
            they just land on the matching tab, so bookmarks don't 404. */}
        <Route path="/reports" element={<PermittedRoute><ReportsHub /></PermittedRoute>} />
        <Route path="/service-report" element={<Navigate to="/reports?group=service&tab=complaint" replace />} />
        <Route path="/analytics/service" element={<Navigate to="/reports?group=service&tab=live" replace />} />
        <Route path="/recurring-chargeable" element={<Navigate to="/reports?group=service&tab=recurring" replace />} />
        <Route path="/audit" element={<PermittedRoute><AuditLog /></PermittedRoute>} />
        <Route path="/partcode-requests" element={<PermittedRoute><PartCodeRequests /></PermittedRoute>} />
        {/* /material-req is folded into /indents per the process-flow consolidation.
            Keep the URL alive so old bookmarks / shared links don't 404. */}
        <Route path="/material-req" element={<Navigate to="/indents" replace />} />
        <Route path="/deliveries" element={<PermittedRoute><Deliveries /></PermittedRoute>} />
        <Route path="/masters" element={<PermittedRoute><Masters /></PermittedRoute>} />
        <Route path="/transactions" element={<PermittedRoute><Transactions /></PermittedRoute>} />
        <Route path="/rfqs" element={<PermittedRoute><RFQs /></PermittedRoute>} />
        <Route path="/petty-cash" element={<PermittedRoute><PettyCash /></PermittedRoute>} />
        <Route path="/containers" element={<PermittedRoute><Containers /></PermittedRoute>} />
        <Route path="/approval-rules" element={<PermittedRoute><ApprovalRules /></PermittedRoute>} />
        <Route path="/petty-cash-approval-rules" element={<PermittedRoute><PettyCashApprovalRules /></PermittedRoute>} />
        <Route path="/monthly-close" element={<PermittedRoute><MonthlyClose /></PermittedRoute>} />
        <Route path="/vendor-coord" element={<PermittedRoute><VendorCoordination /></PermittedRoute>} />
        <Route path="/jo-tracker" element={<PermittedRoute><JobOrderTracker /></PermittedRoute>} />
        <Route path="/container-service-history" element={<PermittedRoute><ContainerServiceHistory /></PermittedRoute>} />
        <Route path="/pm-schedule" element={<PermittedRoute><PmSchedule /></PermittedRoute>} />
        <Route path="/data-actions" element={<PermittedRoute><DataActions /></PermittedRoute>} />
        <Route path="/service-monday-report" element={<PermittedRoute><ServiceMondayReport /></PermittedRoute>} />
        <Route path="/live-status" element={<PermittedRoute><LiveStatus /></PermittedRoute>} />
        <Route path="/jo-tat-analytics" element={<PermittedRoute><TatAnalytics /></PermittedRoute>} />
        <Route path="/job-cost-review" element={<PermittedRoute><JobCostReview /></PermittedRoute>} />
        <Route path="/travel-planning" element={<PermittedRoute><TravelPlanning /></PermittedRoute>} />
        <Route path="/service-requests" element={<PermittedRoute><ServiceRequests /></PermittedRoute>} />
        <Route path="/technicians" element={<PermittedRoute><Technicians /></PermittedRoute>} />
        <Route path="/technicians/:id" element={<PermittedRoute><TechnicianDetail /></PermittedRoute>} />
        <Route path="/ims-containers" element={<PermittedRoute><ImsContainers /></PermittedRoute>} />
        {/* Crystal Public API v1. Key admin is admin-only (the backend router
            enforces requireRole('admin') too); the Integration Kit is docs and
            follows the normal per-user page permission. */}
        <Route path="/api-keys" element={<ApiKeyOwnerOnly><ApiKeys /></ApiKeyOwnerOnly>} />
        {/* Admin-only — sidebar entry is also gated on user.role === 'admin'. */}
        <Route path="/settings" element={<AdminOnly><Settings /></AdminOnly>} />
        <Route path="/tally-sync" element={<TallyAllowed><TallySync /></TallyAllowed>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </DocStackProvider>
  );
}
